// components/Footer.jsx — Phase F premium marketplace footer.
//
// Behavior preserved:
//   - /site-settings fetch
//   - Frontend-only newsletter validation + toast (no backend write)
//   - Filter out '#' / empty social URLs

import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    FaFacebookF, FaLinkedinIn, FaGithub, FaInstagram, FaYoutube,
    FaMapMarkerAlt, FaPhone, FaEnvelope,
    FaShieldAlt, FaLock, FaWallet, FaHeadset,
    FaRobot, FaRegCopy, FaCheck,
} from 'react-icons/fa'
import { AiOutlineTwitter } from 'react-icons/ai'
import { SiTiktok } from 'react-icons/si'
import toast from 'react-hot-toast'

import api from '../api/api'

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const isUsableUrl = (url) => {
    if (!url) return false
    const t = String(url).trim()
    return t && t !== '#' && !/^javascript:/i.test(t)
}

const SOCIAL_ICON = {
    facebook:  <FaFacebookF />,
    twitter:   <AiOutlineTwitter />,
    linkedin:  <FaLinkedinIn />,
    github:    <FaGithub />,
    instagram: <FaInstagram />,
    youtube:   <FaYoutube />,
    tiktok:    <SiTiktok />,
}

// Default link sets — only point to routes that exist in App.jsx.
// Backend can override any of these via /site-settings.
const DEFAULT = {
    footerLogo:        '/images/logo.png',
    footerBrandIntro:  'Rəqəmsal məhsullar, oyun hesabları və etibarlı satıcılar üçün marketplace.',
    footerTrustNote:   'Bütün ödənişlər təhlükəsiz infrastruktur üzərindən işlənir.',
    footerAddress:     'Azərbaycan, Bakı',
    footerPhone:       '+994 50 730 56 79',
    footerEmail:       'itemzo.service@gmail.com',
    footerCopyright:   'ItemZo. Bütün hüquqlar qorunur.',
    footerSocials: [
        { platform: 'facebook',  url: '', active: true },
        { platform: 'instagram', url: '', active: true },
        { platform: 'twitter',   url: '', active: true },
        { platform: 'youtube',   url: '', active: true },
    ],
    footerMarketplaceLinks: [
        { label: 'Bazar',                path: '/market',              active: true },
        { label: 'Mağazalar',            path: '/magazalar',           active: true },
        { label: 'Kampaniyalar',         path: '/kampaniyalar',        active: true },
        { label: 'Çəkilişlər',           path: '/cekilisler',          active: true },
        { label: 'Önə çıxarılan elanlar', path: '/bazar?type=bumped',   active: true },
        { label: 'Həftənin fürsətləri',  path: '/bazar?type=weekly', active: true },
        { label: 'Blog',                 path: '/blogs',               active: true },
        { label: 'Rəylər',               path: '/reviews',             active: true },
    ],
    footerSupportLinks: [
        { label: 'Dəstək mərkəzi',          path: '/support',                 active: true },
        { label: 'Müraciət yarat',          path: '/support/tickets/create',  active: true },
        { label: 'Tez-tez verilən suallar', path: '/fag',                     active: true },
        { label: 'Əlaqə',                   path: '/contact',                 active: true },
    ],
    footerAccountLinks: [
        // Customer-side account shortcuts — auth gating handled at route level.
        { label: 'Giriş',       path: '/login',                active: true },
        { label: 'Qeydiyyat',   path: '/register',             active: true },
        { label: 'Satıcı ol',   path: '/satici-ol',            active: true },
        { label: 'Səbət',       path: '/card',                 active: true },
        { label: 'Hesabım',     path: '/dashboard',            active: true },
        { label: 'Sifarişlərim', path: '/dashboard/my-orders', active: true },
    ],
    footerLegalLinks: [
        // Only routes that exist today — backend can extend.
        { label: 'Tez-tez verilən suallar', path: '/fag',     active: true },
        { label: 'Əlaqə',                   path: '/contact', active: true },
    ],
    footerNewsletterTitle: 'Xəbərlərdən xəbərdar ol',
    footerNewsletterText:  'Yeni məhsul və kampaniyalardan ilk siz xəbər tutun.',

    // ── AI-də Itemzo (admin /site-settings ilə idarə oluna bilər) ──
    aiSectionEnabled: true,
    aiTitle:       'AI-də Itemzo',
    aiDescription: 'Itemzo haqqında süni intellektdən soruşmaq üçün hazır sorğudan istifadə et.',
    aiPrompt:      'Itemzo nədir və rəqəmsal məhsul almaq üçün necə istifadə olunur?',
}

// Generic AI saytları (rəsmi tərəfdaşlıq iddiası YOXDUR — sadəcə istifadəçi linkidir).
// prefill yalnız rəsmi search URL-i olanlar üçün (Perplexity). Digərləri: kopyala + ana səhifə.
const AI_PROVIDERS = [
    { name: 'ChatGPT',    home: 'https://chatgpt.com',        prefill: null },
    { name: 'Gemini',     home: 'https://gemini.google.com',  prefill: null },
    { name: 'Claude',     home: 'https://claude.ai',          prefill: null },
    { name: 'Perplexity', home: 'https://www.perplexity.ai',  prefill: (q) => `https://www.perplexity.ai/search?q=${encodeURIComponent(q)}` },
]

const TRUST_ITEMS = [
    { icon: <FaShieldAlt />, labelKey: 'footer.trustSecure',  tone: 'text-success-600 bg-success-50' },
    { icon: <FaLock />,      labelKey: 'footer.trustSsl',     tone: 'text-brand-600   bg-brand-50'   },
    { icon: <FaWallet />,    labelKey: 'footer.trustBalance', tone: 'text-warning-600 bg-warning-50' },
    { icon: <FaHeadset />,   labelKey: 'footer.trustSupport', tone: 'text-accent-600  bg-accent-50'  },
]

// Maps known default AZ link labels → locale keys. Admin-customised labels
// that aren't in this map fall through unchanged (config is respected).
const FOOTER_LINK_KEY = {
    'Bazar': 'footer.links.market',
    'Mağazalar': 'footer.links.shops',
    'Kampaniyalar': 'footer.links.campaigns',
    'Çəkilişlər': 'footer.links.giveaways',
    'Önə çıxarılan elanlar': 'footer.links.vitrin',
    'Həftənin fürsətləri': 'footer.links.weeklyDeals',
    'Blog': 'footer.links.blog',
    'Rəylər': 'footer.links.reviews',
    'Dəstək mərkəzi': 'footer.links.supportCenter',
    'Müraciət yarat': 'footer.links.createTicket',
    'Tez-tez verilən suallar': 'footer.links.faq',
    'Əlaqə': 'footer.links.contact',
    'Giriş': 'footer.links.login',
    'Qeydiyyat': 'footer.links.register',
    'Satıcı ol': 'footer.links.becomeSeller',
    'Səbət': 'footer.links.cart',
    'Hesabım': 'footer.links.account',
    'Sifarişlərim': 'footer.links.myOrders',
}

// Translate a footer link label only when it matches a known default label;
// otherwise return the (admin-configured) label as-is.
const footerLinkLabel = (t, label) => {
    const key = FOOTER_LINK_KEY[label]
    return key ? t(key, { defaultValue: label }) : label
}

// ─── small inline components ─────────────────────────────────
const ColumnHeading = ({ children }) => (
    <h3 className='mb-3 text-xs font-bold uppercase tracking-wider text-slate-800'>
        {children}
    </h3>
)

const LinkList = ({ items = [] }) => {
    const { t } = useTranslation()
    return (
    <ul className='flex flex-col gap-2 text-sm text-slate-600'>
        {items.map((l, i) => (
            <li key={i}>
                <Link to={l.path} className='hover:text-brand-700 transition-colors'>
                    {footerLinkLabel(t, l.label)}
                </Link>
            </li>
        ))}
    </ul>
    )
}

const TrustStrip = () => {
    const { t } = useTranslation()
    return (
    <ul className='grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-0 rounded-card border border-slate-200 bg-white overflow-hidden divide-x divide-slate-100 md:divide-x-0 md:divide-y'>
        {TRUST_ITEMS.map((it, i) => (
            <li key={i} className='flex items-center gap-2.5 px-4 py-2.5'>
                <span className={`shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-pill ${it.tone}`}>
                    {it.icon}
                </span>
                <span className='text-xs font-semibold text-slate-700'>{t(it.labelKey)}</span>
            </li>
        ))}
    </ul>
    )
}

const NewsletterForm = () => {
    const { t } = useTranslation()
    const [email, setEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const onSubmit = (e) => {
        e.preventDefault()
        if (submitting) return
        if (!isValidEmail(email)) {
            toast.error(t('footer.emailInvalid'))
            return
        }
        setSubmitting(true)
        // Frontend-only: no backend write, no storage.
        setTimeout(() => {
            toast.success(t('footer.thanks'))
            setEmail('')
            setSubmitting(false)
        }, 200)
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className='flex items-stretch overflow-hidden rounded-card border border-slate-200 bg-white shadow-card focus-within:border-brand-500 focus-within:shadow-ring transition-all'
        >
            <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type='email'
                placeholder={t('footer.emailPlaceholder')}
                aria-label={t('footer.emailPlaceholder')}
                disabled={submitting}
                className='h-11 flex-1 bg-transparent px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 disabled:opacity-60'
            />
            <button
                type='submit'
                disabled={submitting}
                className='h-11 inline-flex items-center px-4 bg-brand-600 text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
            >
                {t('footer.subscribe')}
            </button>
        </form>
    )
}

// ─── AI-də Itemzo (kompakt footer vidceti) ───────────────────
// Prompt kopyalanır, sonra AI saytı yeni tabda açılır. Auto-submit YOXDUR
// (yalnız rəsmi search URL-i olan Perplexity prefill edir). Tərəfdaşlıq iddiası YOXDUR.
const FooterAICard = ({ title, description, prompt }) => {
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)

    const doCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(prompt)
            else {
                const ta = document.createElement('textarea')
                ta.value = prompt; ta.style.position = 'fixed'; ta.style.opacity = '0'
                document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
            }
            return true
        } catch { return false }
    }

    const onCopy = async () => {
        if (await doCopy()) { setCopied(true); toast.success(t('footer.ai.toastCopied')); setTimeout(() => setCopied(false), 2000) }
        else toast.error(t('footer.ai.toastCopyFail'))
    }

    // openAiWithPrompt(provider): kopyala → prefill varsa onu, yoxsa ana səhifəni aç → toast
    const openAiWithPrompt = async (provider) => {
        await doCopy()
        const url = provider.prefill ? provider.prefill(prompt) : provider.home
        window.open(url, '_blank', 'noopener,noreferrer')
        toast.success(provider.prefill
            ? t('footer.ai.toastPrepared')
            : t('footer.ai.toastCopiedPaste'))
    }

    return (
        <div className='group rounded-card border border-slate-200 bg-white p-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-card'>
            <div className='mb-1.5 flex items-center gap-1.5'>
                <span className='inline-flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-brand-600 to-indigo-600 text-white'><FaRobot size={11} /></span>
                <h3 className='text-xs font-bold text-slate-800'>{title}</h3>
            </div>
            <p className='text-[11px] leading-snug text-slate-500'>{description}</p>

            {/* prompt preview (bir sətir, kəsilir) + kopyala */}
            <div className='mt-2 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5'>
                <p className='flex-1 truncate text-[10px] text-slate-600' title={prompt}>{prompt}</p>
                <button type='button' onClick={onCopy} aria-label={t('footer.ai.copyAria')}
                    className='shrink-0 inline-flex items-center gap-1 rounded-md bg-brand-600 px-1.5 py-1 text-[9px] font-bold text-white hover:bg-brand-700 transition-colors'>
                    {copied ? <FaCheck size={8} /> : <FaRegCopy size={8} />} {copied ? t('footer.ai.copied') : t('footer.ai.copy')}
                </button>
            </div>

            {/* kompakt AI düymələri (2×2) */}
            <div className='mt-2 grid grid-cols-2 gap-1.5'>
                {AI_PROVIDERS.map(p => (
                    <button key={p.name} type='button' onClick={() => openAiWithPrompt(p)} title={t('footer.ai.askOn', { name: p.name })}
                        className='inline-flex items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-bold text-slate-600 hover:border-brand-300 hover:text-brand-700 hover:bg-brand-50 transition-colors'>
                        {p.name}
                    </button>
                ))}
            </div>

            <p className='mt-2 text-[9px] text-slate-400'>{t('footer.ai.note')}</p>
        </div>
    )
}

// ─── main component ──────────────────────────────────────────
const Footer = () => {
    const { t } = useTranslation()
    const [s, setS] = useState(DEFAULT)

    useEffect(() => {
        api.get('/site-settings')
            .then(({ data }) => { if (data.success) setS(prev => ({ ...prev, ...data.data })) })
            .catch(() => {})
    }, [])

    // For admin-configurable free text: if the value is still the default
    // (admin hasn't overridden it), translate it; otherwise show admin's value.
    const adminText = (value, def, key) => (value && value !== def) ? value : t(key, { defaultValue: def })

    const activeSocials   = (s.footerSocials             || []).filter(x => x.active && isUsableUrl(x.url))
    const activeMarket    = (s.footerMarketplaceLinks    || DEFAULT.footerMarketplaceLinks).filter(x => x.active)
    const activeSupport   = (s.footerSupportLinks        || DEFAULT.footerSupportLinks).filter(x => x.active)
    const activeAccount   = (s.footerAccountLinks        || DEFAULT.footerAccountLinks).filter(x => x.active)
    const activeLegal     = (s.footerLegalLinks          || DEFAULT.footerLegalLinks).filter(x => x.active)

    return (
        <footer className='bg-slate-50 border-t border-slate-200 mt-4 md-lg:pb-20'>
            {/* Trust strip */}
            <div className='mx-auto max-w-marketplace px-4 md-lg:px-5 lg:px-8 pt-6 pb-1'>
                <TrustStrip />
            </div>

            {/* Main 4-column row */}
            <div className='mx-auto max-w-marketplace px-4 md-lg:px-5 lg:px-8 pt-4 pb-10'>
                <div className='grid grid-cols-12 gap-8 md-lg:gap-6'>
                    {/* Brand + newsletter column */}
                    <section className='col-span-4 md-lg:col-span-12'>
                        <Link to='/' className='inline-block'>
                            <img
                                className='h-10 w-auto object-contain'
                                src={s.footerLogo}
                                alt='ItemZo'
                                onError={e => e.currentTarget.src = '/images/logo.png'}
                            />
                        </Link>
                        <p className='mt-3 text-sm text-slate-600 max-w-sm'>
                            {adminText(s.footerBrandIntro, DEFAULT.footerBrandIntro, 'footer.brandIntro')}
                        </p>

                        <ul className='mt-4 flex flex-col gap-2 text-sm text-slate-600'>
                            <li className='flex items-start gap-2'>
                                <FaMapMarkerAlt className='text-brand-500 mt-0.5 shrink-0' size={13} />
                                <span>{adminText(s.footerAddress, DEFAULT.footerAddress, 'footer.address')}</span>
                            </li>
                            <li className='flex items-center gap-2'>
                                <FaPhone className='text-brand-500 shrink-0' size={12} />
                                <a
                                    href={`tel:${String(s.footerPhone).replace(/\s/g, '')}`}
                                    className='hover:text-brand-700 transition-colors'
                                >
                                    {s.footerPhone}
                                </a>
                            </li>
                            <li className='flex items-center gap-2'>
                                <FaEnvelope className='text-brand-500 shrink-0' size={12} />
                                <a
                                    href={`mailto:${s.footerEmail}`}
                                    className='hover:text-brand-700 transition-colors'
                                >
                                    {s.footerEmail}
                                </a>
                            </li>
                        </ul>

                        {/* Newsletter — tight, no extra heading bulk */}
                        <div className='mt-5 max-w-sm'>
                            <p className='mb-2 text-xs font-semibold text-slate-700'>
                                {adminText(s.footerNewsletterTitle, DEFAULT.footerNewsletterTitle, 'footer.newsletterTitle')}
                            </p>
                            <NewsletterForm />
                        </div>

                        {activeSocials.length > 0 && (
                            <ul className='mt-5 flex items-center gap-2'>
                                {activeSocials.map((soc, i) => (
                                    <li key={i}>
                                        <a
                                            href={soc.url}
                                            target='_blank'
                                            rel='noreferrer'
                                            aria-label={soc.platform}
                                            className='inline-flex h-8 w-8 items-center justify-center rounded-pill border border-slate-200 bg-white text-slate-500 hover:bg-brand-600 hover:text-white hover:border-brand-600 transition-colors'
                                        >
                                            {SOCIAL_ICON[soc.platform] || <FaFacebookF />}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* AI-də Itemzo — kompakt vidcet (sosial linklərin altında) */}
                        {s.aiSectionEnabled !== false && (
                            <div className='mt-5 max-w-sm'>
                                <FooterAICard
                                    title={adminText(s.aiTitle, DEFAULT.aiTitle, 'footer.ai.title')}
                                    description={adminText(s.aiDescription, DEFAULT.aiDescription, 'footer.ai.description')}
                                    prompt={adminText(s.aiPrompt, DEFAULT.aiPrompt, 'footer.ai.prompt')}
                                />
                            </div>
                        )}
                    </section>

                    {/* Marketplace column */}
                    <section className='col-span-3 md-lg:col-span-4 md:col-span-6 sm:col-span-12'>
                        <ColumnHeading>{t('footer.marketplace')}</ColumnHeading>
                        <LinkList items={activeMarket} />
                    </section>

                    {/* Support column */}
                    <section className='col-span-2 md-lg:col-span-4 md:col-span-6 sm:col-span-12'>
                        <ColumnHeading>{t('footer.support')}</ColumnHeading>
                        <LinkList items={activeSupport} />
                    </section>

                    {/* Account column */}
                    <section className='col-span-3 md-lg:col-span-4 md:col-span-12 sm:col-span-12'>
                        <ColumnHeading>{t('footer.account')}</ColumnHeading>
                        <LinkList items={activeAccount} />
                    </section>
                </div>
            </div>

            {/* Bottom legal row */}
            <div className='border-t border-slate-200'>
                <div className='mx-auto flex max-w-marketplace flex-wrap items-center justify-between gap-3 px-4 sm:px-4 md-lg:px-5 lg:px-8 py-4 text-xs text-slate-500'>
                    <span>? {new Date().getFullYear()} {adminText(s.footerCopyright, DEFAULT.footerCopyright, 'footer.copyright')}</span>
                    {activeLegal.length > 0 && (
                        <ul className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                            {activeLegal.map((l, i) => (
                                <li key={i}>
                                    <Link to={l.path} className='hover:text-brand-700 transition-colors'>
                                        {footerLinkLabel(t, l.label)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </footer>
    )
}

export default Footer
